import { Component, inject, OnDestroy, OnInit, signal, viewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, combineLatest, forkJoin, map, Observable, of, Subscription, switchMap, throwError } from 'rxjs';
import { BreadcrumbsService } from '../../../../shared/breadcrumbs/breadcrumbs.service';
import { BottomNavService, BottomNavUpdateButtonsList } from '../../../../shared/bottom-nav/bottom-nav.service';
import { ButtonStyles, ButtonTypes } from '../../../../button/button.component';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';
import {
  DatatableBadge,
  DatatableColumn,
  DatatableOnRowFieldChange,
  DatatableRow,
  DatatableRowActionButton,
} from '../../../../shared/tables/datatable/datatable.models';
import {
  DatatableFieldTypes,
  DatatableTypeaheadField,
} from '../../../../shared/tables/datatable/datatable-field.models';
import { MigrationsAssessmentsContext } from '../../../services/migrations-assessments.context';
import { TranslateService } from '@ngx-translate/core';
import {
  AccountType,
  CallQueueExceptionHandling,
  CallQueueExceptionHandlingAction,
  CallQueueExceptionHandlingTargetType,
  MicrosoftTeamsCallQueue,
  MicrosoftTeamsUserResult,
  NoAgentApplyTo,
  NoAgentSettings,
  OverflowSettings,
  RingCentralCallQueueMembership,
  RingCentralCallQueueReportEntry,
  TimeoutSettings,
} from '../../../../shared/models/generated/ziroModels';
import { RingCentralCallQueuesContext } from '../ring-central-call-queues.context';
import { TelephoneNumberFilter } from '../../../../shared/filters/telephone-number.filter';
import { AudioFileResource, AudioPostResponse } from '../../../../shared/resources/audio-file.resource';
import { CallQueuesResource } from '../../../../shared/resources/call-queues.resource';
import {
  MicrosoftTeamsUserSearchResource,
  TeamsUserSearchScope,
} from '../../../../helpdesk/shared/resources/microsoft-teams-user-search.resource';
import { TeamsCallQueuesFieldConfigResource } from '../../../../helpdesk/shared/resources/teams-call-queues-field-config.resource';
import { LanguageOption } from '../../../../teams-call-queues/teams-call-queue/teams-call-queue-edit.component';
import { ToastService } from '../../../../shared/services/toast.service';
import { ZiroFormsUpdate, ZiroFormsValidationState } from '../../../../forms/ziro-forms-models';
import {
  CallQueueExceptionHandlingFormComponent,
  defaultUiExceptionHandlingSettings,
  ExceptionHandlingPromptType,
  ExceptionHandlingRedirectType,
  ExceptionHandlingSectionType,
  mapUiToExceptionHandling,
  UiExceptionHandlingSettings,
} from '../../../../teams-call-queues/teams-call-queue/teams-call-queue-form/exception-handling-form/call-queue-exception-handling-form.component';

interface CallQueueMemberMappingRow extends DatatableRow {
  memberId: string;
  name: string;
  email: string;
  phoneNumbers: string;
  teamsUpn: string;
  teamsPhoneNumber: string;
}

interface MemberMatch {
  member: RingCentralCallQueueMembership;
  teamsUser: MicrosoftTeamsUserResult | undefined;
}

@Component({
  selector: 'ziro-ring-central-call-queue-teams-mapping',
  templateUrl: './ring-central-call-queue-teams-mapping.component.html',
  styleUrls: ['./ring-central-call-queue-teams-mapping.component.scss'],
  standalone: false,
})
export class RingCentralCallQueueTeamsMappingComponent implements OnInit, OnDestroy {
  private _ringCentralCallQueuesContext = inject(RingCentralCallQueuesContext);
  private _migrationsAssessmentsContext = inject(MigrationsAssessmentsContext);
  private _teamsUsersSearchResource = inject(MicrosoftTeamsUserSearchResource);
  private _teamsCallQueuesFieldConfigResource = inject(TeamsCallQueuesFieldConfigResource);
  private _callQueuesResource = inject(CallQueuesResource);
  private _audioFileResource = inject(AudioFileResource);
  private _breadcrumbsService = inject(BreadcrumbsService);
  private _bottomNavService = inject(BottomNavService);
  private _toastService = inject(ToastService);
  private _translateService = inject(TranslateService);
  private _telephoneNumberFilter = inject(TelephoneNumberFilter);
  private _router = inject(Router);
  private _route = inject(ActivatedRoute);

  ziroIcons = ZiroIcons;
  ExceptionHandlingSectionType = ExceptionHandlingSectionType;
  ZiroFormsValidationState = ZiroFormsValidationState;
  isLoading = true;
  tenantId: string;
  exportId: string;
  callQueueId: string;
  callQueue: RingCentralCallQueueReportEntry;
  callQueueEntity: MicrosoftTeamsCallQueue;
  memberColumns: DatatableColumn<CallQueueMemberMappingRow>[] = [];
  memberRows: CallQueueMemberMappingRow[] = [];
  languageOptions: LanguageOption[] = [];
  greetingPreviewUrl: string;
  greetingDownloadName: string;
  musicOnHoldPreviewUrl: string;
  musicOnHoldDownloadName: string;

  overflowFormComponent = viewChild<CallQueueExceptionHandlingFormComponent>('overflowForm');
  timeoutFormComponent = viewChild<CallQueueExceptionHandlingFormComponent>('timeoutForm');
  noAgentsFormComponent = viewChild<CallQueueExceptionHandlingFormComponent>('noAgentsForm');

  activeExceptionTab = ExceptionHandlingSectionType.OVERFLOW;
  exceptionTabsValidationState = {
    [ExceptionHandlingSectionType.OVERFLOW]: signal(ZiroFormsValidationState.VALID),
    [ExceptionHandlingSectionType.TIMEOUT]: signal(ZiroFormsValidationState.VALID),
    [ExceptionHandlingSectionType.NO_AGENTS]: signal(ZiroFormsValidationState.VALID),
  };

  uiOverflowSettings: UiExceptionHandlingSettings;
  uiTimeoutSettings: UiExceptionHandlingSettings;
  uiNoAgentSettings: UiExceptionHandlingSettings;

  private _exportName: string;
  private _formEntity: MicrosoftTeamsCallQueue;
  private _memberMatches: MemberMatch[] = [];
  private _formValidationState = ZiroFormsValidationState.INVALID;
  private _membersValidationState = ZiroFormsValidationState.PENDING;
  private _exceptionTabBySettingsKey = {
    uiOverflowSettings: ExceptionHandlingSectionType.OVERFLOW,
    uiTimeoutSettings: ExceptionHandlingSectionType.TIMEOUT,
    uiNoAgentSettings: ExceptionHandlingSectionType.NO_AGENTS,
  } as const;
  private _subscriptions = new Subscription();

  ngOnInit(): void {
    ({ tenantId: this.tenantId, id: this.exportId, callQueueId: this.callQueueId } = this._route.snapshot.params);

    this._initBottomNav();
    this._initMemberColumns();

    const sub = combineLatest([
      this._migrationsAssessmentsContext.state$,
      this._ringCentralCallQueuesContext.state$,
      this._teamsCallQueuesFieldConfigResource.get(Number(this.tenantId)),
    ])
      .pipe(
        switchMap(([allExports, callQueues, fieldConfig]) => {
          this._exportName = allExports.ringCentralExports.find(
            (ringExport) => ringExport.exportId === this.exportId,
          )?.name;
          this.callQueue = callQueues.find((queue) => queue.id === this.callQueueId);
          this.callQueueEntity = { ...this.callQueue.toTeamsCallQueue.toTeams, callQueueId: null };
          this._formEntity = this.callQueueEntity;
          this.uiOverflowSettings = this._mapOverflowSettingsToUi(this.callQueueEntity.overflowSettings);
          this.uiTimeoutSettings = this._mapTimeoutSettingsToUi(this.callQueueEntity.timeoutSettings);
          this.uiNoAgentSettings = this._mapNoAgentSettingsToUi(this.callQueueEntity.noAgentSettings);
          this.languageOptions = (fieldConfig?.languages ?? []).map((language) => ({
            label: language.displayName,
            value: language.code,
          }));
          this._initBreadcrumbs();
          this._loadAudioPreviews();

          return this._resolveMembers(this.callQueue.callQueueMembership);
        }),
      )
      .subscribe((memberMatches) => {
        this._memberMatches = memberMatches;
        this._buildMemberRows(memberMatches);
        this.isLoading = false;
      });
    this._subscriptions.add(sub);
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  onFormUpdate(update: ZiroFormsUpdate<MicrosoftTeamsCallQueue>): void {
    this._formEntity = update.new;
    this._formValidationState = update.valid;
  }

  onMembersValidationUpdate(state: ZiroFormsValidationState): void {
    this._membersValidationState = state;
  }

  onMemberTeamsUpnChange(change: DatatableOnRowFieldChange<CallQueueMemberMappingRow>): void {
    if (change.columnId !== 'teamsUpn' || !change.newValue) {
      return;
    }

    const rowIndex = this.memberRows.findIndex((row) => row.memberId === change.row.memberId);
    if (rowIndex === -1) {
      return;
    }

    if (this._memberMatches[rowIndex].teamsUser?.userPrincipalName === change.newValue) {
      return;
    }

    const sub = this._teamsUsersSearchResource
      .searchByUserPrincipalName(this.tenantId, TeamsUserSearchScope.CALL_QUEUE_MANAGEMENT, change.newValue)
      .subscribe((results) => {
        const teamsUser = results[0];
        if (!teamsUser) {
          return;
        }

        this._memberMatches[rowIndex] = { ...this._memberMatches[rowIndex], teamsUser };
        this.memberRows = this.memberRows.map((row, index) =>
          index === rowIndex
            ? {
                ...row,
                teamsUpn: teamsUser.userPrincipalName,
                teamsPhoneNumber: teamsUser.lineUri ? this._telephoneNumberFilter.transform(teamsUser.lineUri) : '',
                fields: { ...row.fields, teamsUpn: this._buildTeamsUpnField(teamsUser) },
              }
            : row,
        );
      });
    this._subscriptions.add(sub);
  }

  getAgentAlertTime(): number {
    return this._formEntity.agentAlertTime;
  }

  setExceptionHandlingSettings(
    update: ZiroFormsUpdate<UiExceptionHandlingSettings>,
    settingsKey: 'uiOverflowSettings' | 'uiTimeoutSettings' | 'uiNoAgentSettings',
  ): void {
    if (update.valid === ZiroFormsValidationState.VALID || update.valid === ZiroFormsValidationState.INVALID) {
      this.exceptionTabsValidationState[this._exceptionTabBySettingsKey[settingsKey]].set(update.valid);
    }
    this[settingsKey] = update.new;
  }

  getExceptionHandlingSubtext(): string {
    if (this.activeExceptionTab === ExceptionHandlingSectionType.TIMEOUT) {
      return 'tkey;teams_call_queue.exception_handling.timeout.help_text';
    }

    if (this.activeExceptionTab === ExceptionHandlingSectionType.NO_AGENTS) {
      return 'tkey;teams_call_queue.exception_handling.no_agents.help_text';
    }

    return 'tkey;teams_call_queue.exception_handling.overflow.help_text';
  }

  get attentionMessages(): string[] {
    const messages: string[] = [];

    const invalidMemberCount = this._memberMatches.filter(
      (memberMatch) =>
        !memberMatch.teamsUser || !memberMatch.teamsUser.enterpriseVoiceEnabled || !memberMatch.teamsUser.lineUri,
    ).length;
    if (invalidMemberCount) {
      messages.push(
        this._translateService.instant(
          'tkey;migrations.ring_central.call_queues.teams_mapping.attention_banner.members_invalid',
          { count: invalidMemberCount },
        ),
      );
    }

    if (this._formValidationState === ZiroFormsValidationState.INVALID) {
      messages.push(
        this._translateService.instant(
          'tkey;migrations.ring_central.call_queues.teams_mapping.attention_banner.settings_invalid',
        ),
      );
    }

    const exceptionHandlingSectionHeaderKeys: Record<ExceptionHandlingSectionType, string> = {
      [ExceptionHandlingSectionType.OVERFLOW]: 'tkey;teams_call_queue.exception_handling.overflow.header',
      [ExceptionHandlingSectionType.TIMEOUT]: 'tkey;teams_call_queue.exception_handling.timeout.header',
      [ExceptionHandlingSectionType.NO_AGENTS]: 'tkey;teams_call_queue.exception_handling.no_agents.header',
    };
    [
      ExceptionHandlingSectionType.OVERFLOW,
      ExceptionHandlingSectionType.TIMEOUT,
      ExceptionHandlingSectionType.NO_AGENTS,
    ]
      .filter((sectionType) => this.exceptionTabsValidationState[sectionType]() === ZiroFormsValidationState.INVALID)
      .forEach((sectionType) => {
        messages.push(
          this._translateService.instant(
            'tkey;migrations.ring_central.call_queues.teams_mapping.attention_banner.exception_handling_invalid',
            { section: this._translateService.instant(exceptionHandlingSectionHeaderKeys[sectionType]) },
          ),
        );
      });

    return messages;
  }

  private _loadAudioPreviews(): void {
    const { greetingFilename, musicOnHoldFilename } = this.callQueue.audioInformation;

    if (greetingFilename) {
      this._audioFileResource
        .getAudioFile(this.tenantId, 'ring-central', greetingFilename, this.exportId)
        .subscribe((file) => {
          this.greetingPreviewUrl = URL.createObjectURL(file);
          this.greetingDownloadName = file.name;
        });
    }

    if (musicOnHoldFilename) {
      this._audioFileResource
        .getAudioFile(this.tenantId, 'ring-central', musicOnHoldFilename, this.exportId)
        .subscribe((file) => {
          this.musicOnHoldPreviewUrl = URL.createObjectURL(file);
          this.musicOnHoldDownloadName = file.name;
        });
    }
  }

  private _mapOverflowSettingsToUi(overflowSettings: OverflowSettings): UiExceptionHandlingSettings {
    const uiSettings = this._mapExceptionHandlingToUi(
      ExceptionHandlingSectionType.OVERFLOW,
      overflowSettings.exceptionHandling,
    );
    uiSettings.overflowThreshold = overflowSettings.overflowThreshold;
    return uiSettings;
  }

  private _mapTimeoutSettingsToUi(timeoutSettings: TimeoutSettings): UiExceptionHandlingSettings {
    const uiSettings = this._mapExceptionHandlingToUi(
      ExceptionHandlingSectionType.TIMEOUT,
      timeoutSettings.exceptionHandling,
    );
    uiSettings.timeoutMinutes = Math.floor(timeoutSettings.timeoutThresholdInSeconds / 60);
    uiSettings.timeoutSeconds = timeoutSettings.timeoutThresholdInSeconds % 60;
    return uiSettings;
  }

  private _mapNoAgentSettingsToUi(noAgentSettings: NoAgentSettings): UiExceptionHandlingSettings {
    const uiSettings = this._mapExceptionHandlingToUi(
      ExceptionHandlingSectionType.NO_AGENTS,
      noAgentSettings.exceptionHandling,
    );
    uiSettings.noAgentApplyToNewCallsOnly = noAgentSettings.noAgentApplyTo === NoAgentApplyTo.NEW_CALLS;
    return uiSettings;
  }

  /**
   * ToTeamsCallQueueMapper (backend) only ever emits DISCONNECT, QUEUE, REDIRECT+SHARED_MAILBOX (with no target, since
   * the mailbox doesn't exist until the resource account is created) or REDIRECT+EXTERNAL_PHONE (with a tel: target).
   */
  private _mapExceptionHandlingToUi(
    sectionType: ExceptionHandlingSectionType,
    exceptionHandling: CallQueueExceptionHandling,
  ): UiExceptionHandlingSettings {
    const uiSettings = defaultUiExceptionHandlingSettings(sectionType);

    if (!exceptionHandling || exceptionHandling.action === CallQueueExceptionHandlingAction.UNKNOWN) {
      return uiSettings;
    }

    uiSettings.action = exceptionHandling.action;
    uiSettings.audioPromptFileGuid = exceptionHandling.audioPromptFileGuid;
    uiSettings.ttsPrompt = exceptionHandling.ttsPrompt;
    uiSettings.promptType = exceptionHandling.audioPromptFileGuid
      ? ExceptionHandlingPromptType.AUDIO_FILE
      : exceptionHandling.ttsPrompt
        ? ExceptionHandlingPromptType.TTS_PROMPT
        : ExceptionHandlingPromptType.NONE;

    if (exceptionHandling.action !== CallQueueExceptionHandlingAction.REDIRECT) {
      return uiSettings;
    }

    if (exceptionHandling.targetType === CallQueueExceptionHandlingTargetType.EXTERNAL_PHONE) {
      uiSettings.redirectType = ExceptionHandlingRedirectType.EXTERNAL_PHONE;
      uiSettings.externalPhoneNumber = exceptionHandling.target.split('tel:').pop();
    } else if (exceptionHandling.targetType === CallQueueExceptionHandlingTargetType.SHARED_MAILBOX) {
      uiSettings.redirectType = ExceptionHandlingRedirectType.SHARED_MAILBOX;
      if (uiSettings.promptType === ExceptionHandlingPromptType.NONE) {
        uiSettings.promptType = ExceptionHandlingPromptType.TTS_PROMPT;
      }
      if (exceptionHandling.sharedVoicemailSettings) {
        uiSettings.enableTranscription = exceptionHandling.sharedVoicemailSettings.enableSharedVoicemailTranscription;
        uiSettings.suppressSystemMessage =
          exceptionHandling.sharedVoicemailSettings.enableSharedVoicemailSystemPromptSuppression;
      }
    }

    return uiSettings;
  }

  private _resolveMembers(membership: RingCentralCallQueueMembership[]): Observable<MemberMatch[]> {
    if (!membership.length) {
      return of([]);
    }

    return forkJoin(
      membership.map((member) =>
        member.associatedUser?.email
          ? this._teamsUsersSearchResource
              .searchByUserPrincipalName(
                this.tenantId,
                TeamsUserSearchScope.CALL_QUEUE_MANAGEMENT,
                member.associatedUser.email,
              )
              .pipe(map((results): MemberMatch => ({ member, teamsUser: results[0] })))
          : of<MemberMatch>({ member, teamsUser: undefined }),
      ),
    );
  }

  private _initMemberColumns(): void {
    this.memberColumns = [
      {
        name: 'name',
        label: 'tkey;migrations.ring_central.call_queues.teams_mapping.members.table.name.label',
        columnCharacterWidth: 25,
      },
      {
        name: 'email',
        label: 'tkey;migrations.ring_central.call_queues.teams_mapping.members.table.email.label',
        columnCharacterWidth: 30,
      },
      {
        name: 'phoneNumbers',
        label: 'tkey;migrations.ring_central.call_queues.teams_mapping.members.table.phone_numbers.label',
        columnCharacterWidth: 25,
      },
      {
        name: 'teamsUpn',
        label: 'tkey;migrations.ring_central.call_queues.teams_mapping.members.table.teams_user.label',
        columnCharacterWidth: 30,
      },
      {
        name: 'teamsPhoneNumber',
        label: 'tkey;migrations.ring_central.call_queues.teams_mapping.members.table.teams_phone_number.label',
        columnCharacterWidth: 25,
      },
    ];
  }

  private _buildMemberRows(memberMatches: MemberMatch[]): void {
    const phoneNumbersColumnCharacterWidth = this.memberColumns.find(
      (column) => column.name === 'phoneNumbers',
    ).columnCharacterWidth;

    this.memberRows = memberMatches.map((memberMatch, index) => {
      const { member, teamsUser } = memberMatch;

      const deleteAction: DatatableRowActionButton<CallQueueMemberMappingRow> = {
        dataAutomation: 'ring-central-call-queue-teams-mapping-members-table-delete-member',
        type: ButtonTypes.BUTTON,
        style: ButtonStyles.OUTLINE_DANGER,
        icon: ZiroIcons.DELETE_SMALL,
        onClick: () => this._deleteMember(memberMatch),
      };

      const phoneNumbers = member.callQueueMemberDirectNumbers.map((directNumber) =>
        this._telephoneNumberFilter.transform(directNumber.number),
      );

      return {
        memberId: `${index}-${member.number}`,
        name: [member.associatedUser?.firstName, member.associatedUser?.lastName].filter(Boolean).join(' '),
        email: member.associatedUser?.email,
        phoneNumbers: phoneNumbers.join(', '),
        teamsUpn: teamsUser?.userPrincipalName ?? '',
        teamsPhoneNumber: teamsUser?.lineUri ? this._telephoneNumberFilter.transform(teamsUser.lineUri) : '',
        actions: [deleteAction],
        badges: {
          phoneNumbers: {
            values: this._buildPhoneNumberBadges(phoneNumbers, phoneNumbersColumnCharacterWidth),
          },
        },
        fields: {
          teamsUpn: this._buildTeamsUpnField(teamsUser),
        },
      };
    });
  }

  private _buildTeamsUpnField(teamsUser: MicrosoftTeamsUserResult | undefined): DatatableTypeaheadField {
    const isNotFound = !teamsUser;
    const isInvalid = !!teamsUser && (!teamsUser.enterpriseVoiceEnabled || !teamsUser.lineUri);

    return {
      type: DatatableFieldTypes.TYPEAHEAD,
      value: teamsUser?.userPrincipalName ?? '',
      minSearchTermLength: 3,
      options: (query: string): Observable<string[]> =>
        this._teamsUsersSearchResource
          .search(this.tenantId, TeamsUserSearchScope.CALL_QUEUE_MANAGEMENT, query, null, 250, AccountType.USER)
          .pipe(map((users) => users.map((user) => user.userPrincipalName))),
      validation: [
        {
          validator: (value: string): ZiroFormsValidationState =>
            value && !isInvalid ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID,
          message: (): string =>
            this._translateService.instant(
              isNotFound
                ? 'tkey;migrations.ring_central.call_queues.teams_mapping.members.table.teams_user.not_found.tooltip'
                : 'tkey;migrations.ring_central.call_queues.teams_mapping.members.table.teams_user.invalid.tooltip',
            ),
        },
      ],
    };
  }

  private _buildPhoneNumberBadges(
    phoneNumbers: string[],
    maxChars: number,
  ): DatatableBadge<CallQueueMemberMappingRow>[] {
    if (!phoneNumbers.length) {
      return [];
    }

    const totalChars = phoneNumbers.reduce((sum, phoneNumber) => sum + phoneNumber.length, 0);
    if (totalChars <= maxChars) {
      return phoneNumbers.map((phoneNumber) => ({ label: phoneNumber }));
    }

    const moreTkey = this._translateService.instant('tkey;datatable.badge.more.label');
    const visibleCount = Math.min(2, phoneNumbers.length);
    const badges: DatatableBadge<CallQueueMemberMappingRow>[] = phoneNumbers
      .slice(0, visibleCount)
      .map((phoneNumber) => ({
        label: phoneNumber,
        showTooltipOnTruncate: true,
      }));

    const remainingCount = phoneNumbers.length - visibleCount;
    if (remainingCount > 0) {
      badges.push({
        label: `${remainingCount}${moreTkey}`,
        hardcodedTooltip: this._buildPhoneNumberBadgeTooltipMarkup(phoneNumbers.slice(visibleCount)),
      });
    }

    return badges;
  }

  private _buildPhoneNumberBadgeTooltipMarkup(phoneNumbers: string[]): string {
    const formattedPhoneNumbers = phoneNumbers.map((phoneNumber) => `<li>${phoneNumber}</li>`).join('');
    return `<ul class="text-start mb-0 p-0">${formattedPhoneNumbers}</ul>`;
  }

  private _deleteMember(memberMatch: MemberMatch): void {
    this._memberMatches = this._memberMatches.filter((match) => match !== memberMatch);
    this._buildMemberRows(this._memberMatches);
  }

  private _initBreadcrumbs(): void {
    if (!this.callQueue || !this._exportName) {
      return;
    }
    this._breadcrumbsService.updateBreadcrumbs([
      { label: 'tkey;migrations.title', routerLink: true, url: `/migrations/tenants/${this.tenantId}` },
      {
        label: this._translateService.instant('tkey;migrations.number_report.dashboard.breadcrumb', {
          name: this._exportName,
        }),
        routerLink: true,
        url: `/migrations/tenants/${this.tenantId}/ring-central/${this.exportId}`,
      },
      {
        label: 'tkey;migrations.ring_central.call_queues.title',
        routerLink: true,
        url: `/migrations/tenants/${this.tenantId}/ring-central/${this.exportId}/call-queues`,
      },
      {
        label: this.callQueue.name,
        routerLink: true,
        url: `/migrations/tenants/${this.tenantId}/ring-central/${this.exportId}/call-queues/${this.callQueueId}`,
      },
      { label: 'tkey;migrations.ring_central.call_queues.teams_mapping.breadcrumb' },
    ]);
  }

  private _initBottomNav(): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonsList([
        {
          id: 'back-button',
          dataAutomation: 'ring-central-call-queue-teams-mapping-back-button',
          label: 'tkey;global.button.back.text',
          buttonClass: ButtonStyles.DEFAULT,
          icon: ZiroIcons.BACK_CIRCLE,
          cb: () => this._router.navigate(['..'], { relativeTo: this._route }),
        },
        {
          id: 'save-button',
          dataAutomation: 'ring-central-call-queue-teams-mapping-save-button',
          label: 'tkey;global.button.save.text',
          icon: ZiroIcons.SAVE_SUCCESS,
          buttonClass: ButtonStyles.PRIMARY,
          cb: () => this._onSaveClick(),
        },
      ]),
    );
  }

  private _onSaveClick(): void {
    const isOverflowValid = this.overflowFormComponent().validateAndShowErrors();
    const isTimeoutValid = this.timeoutFormComponent().validateAndShowErrors();
    const isNoAgentsValid = this.noAgentsFormComponent().validateAndShowErrors();
    this.exceptionTabsValidationState[ExceptionHandlingSectionType.OVERFLOW].set(
      isOverflowValid ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID,
    );
    this.exceptionTabsValidationState[ExceptionHandlingSectionType.TIMEOUT].set(
      isTimeoutValid ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID,
    );
    this.exceptionTabsValidationState[ExceptionHandlingSectionType.NO_AGENTS].set(
      isNoAgentsValid ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID,
    );

    const firstInvalidExceptionTab = [
      ExceptionHandlingSectionType.OVERFLOW,
      ExceptionHandlingSectionType.TIMEOUT,
      ExceptionHandlingSectionType.NO_AGENTS,
    ].find((tab) => this.exceptionTabsValidationState[tab]() === ZiroFormsValidationState.INVALID);
    if (firstInvalidExceptionTab) {
      this.activeExceptionTab = firstInvalidExceptionTab;
    }

    const isValid =
      this._formValidationState === ZiroFormsValidationState.VALID &&
      this._membersValidationState === ZiroFormsValidationState.VALID &&
      isOverflowValid &&
      isTimeoutValid &&
      isNoAgentsValid;
    this._bottomNavService.setBottomNavValidationError(!isValid);

    if (!isValid) {
      return;
    }

    this._bottomNavService.setButtonPendingState('save-button', true);
    this._bottomNavService.setButtonDisabledState('back-button', true);

    const tenantId = this.tenantId;
    const exportId = this.exportId;
    const audioInformation = this.callQueue.audioInformation;
    const audioUploads: Observable<{
      target:
        | 'greetingAudioId'
        | 'musicOnHoldAudioId'
        | 'overflowAudioPromptFileGuid'
        | 'timeoutAudioPromptFileGuid'
        | 'noAgentAudioPromptFileGuid';
      id: string;
    }>[] = [];

    if (audioInformation.greetingFilename) {
      audioUploads.push(
        this._audioFileResource
          .getAudioFile(tenantId, 'ring-central', audioInformation.greetingFilename, exportId)
          .pipe(
            switchMap((file) => this._audioFileResource.createAudioFile(tenantId, 'call-queues', file)),
            map((response: AudioPostResponse) => ({ target: 'greetingAudioId' as const, id: response.id })),
          ),
      );
    }

    if (audioInformation.musicOnHoldFilename) {
      audioUploads.push(
        this._audioFileResource
          .getAudioFile(tenantId, 'ring-central', audioInformation.musicOnHoldFilename, exportId)
          .pipe(
            switchMap((file) => this._audioFileResource.createAudioFile(tenantId, 'call-queues', file)),
            map((response: AudioPostResponse) => ({ target: 'musicOnHoldAudioId' as const, id: response.id })),
          ),
      );
    }

    if (
      this.uiOverflowSettings.promptType === ExceptionHandlingPromptType.AUDIO_FILE &&
      this.uiOverflowSettings.audioPromptUpload
    ) {
      audioUploads.push(
        this._audioFileResource
          .createAudioFile(tenantId, 'call-queues', this.uiOverflowSettings.audioPromptUpload)
          .pipe(
            map((response: AudioPostResponse) => ({ target: 'overflowAudioPromptFileGuid' as const, id: response.id })),
          ),
      );
    }

    if (
      this.uiTimeoutSettings.promptType === ExceptionHandlingPromptType.AUDIO_FILE &&
      this.uiTimeoutSettings.audioPromptUpload
    ) {
      audioUploads.push(
        this._audioFileResource
          .createAudioFile(tenantId, 'call-queues', this.uiTimeoutSettings.audioPromptUpload)
          .pipe(
            map((response: AudioPostResponse) => ({ target: 'timeoutAudioPromptFileGuid' as const, id: response.id })),
          ),
      );
    }

    if (
      this.uiNoAgentSettings.promptType === ExceptionHandlingPromptType.AUDIO_FILE &&
      this.uiNoAgentSettings.audioPromptUpload
    ) {
      audioUploads.push(
        this._audioFileResource
          .createAudioFile(tenantId, 'call-queues', this.uiNoAgentSettings.audioPromptUpload)
          .pipe(
            map((response: AudioPostResponse) => ({ target: 'noAgentAudioPromptFileGuid' as const, id: response.id })),
          ),
      );
    }

    const sub = (audioUploads.length ? forkJoin(audioUploads) : of([]))
      .pipe(
        map((uploadResults) => {
          let audioSettings = this._formEntity.audioSettings;
          let overflowAudioPromptFileGuid = this.uiOverflowSettings.audioPromptFileGuid;
          let timeoutAudioPromptFileGuid = this.uiTimeoutSettings.audioPromptFileGuid;
          let noAgentAudioPromptFileGuid = this.uiNoAgentSettings.audioPromptFileGuid;

          uploadResults.forEach((uploadResult) => {
            switch (uploadResult.target) {
              case 'greetingAudioId':
              case 'musicOnHoldAudioId':
                audioSettings = { ...audioSettings, [uploadResult.target]: uploadResult.id };
                break;
              case 'overflowAudioPromptFileGuid':
                overflowAudioPromptFileGuid = uploadResult.id;
                break;
              case 'timeoutAudioPromptFileGuid':
                timeoutAudioPromptFileGuid = uploadResult.id;
                break;
              case 'noAgentAudioPromptFileGuid':
                noAgentAudioPromptFileGuid = uploadResult.id;
                break;
            }
          });

          const userGuids = this._memberMatches
            .filter((memberMatch) => memberMatch.teamsUser)
            .map((memberMatch) => memberMatch.teamsUser.guid);

          const overflowSettings: OverflowSettings = {
            overflowThreshold: Number(this.uiOverflowSettings.overflowThreshold),
            exceptionHandling: mapUiToExceptionHandling(this.uiOverflowSettings, overflowAudioPromptFileGuid),
          };
          const timeoutSettings: TimeoutSettings = {
            timeoutThresholdInSeconds:
              Number(this.uiTimeoutSettings.timeoutMinutes) * 60 + Number(this.uiTimeoutSettings.timeoutSeconds),
            exceptionHandling: mapUiToExceptionHandling(this.uiTimeoutSettings, timeoutAudioPromptFileGuid),
          };
          const noAgentSettings: NoAgentSettings = {
            noAgentApplyTo: this.uiNoAgentSettings.noAgentApplyToNewCallsOnly
              ? NoAgentApplyTo.NEW_CALLS
              : NoAgentApplyTo.ALL_CALLS,
            exceptionHandling: mapUiToExceptionHandling(this.uiNoAgentSettings, noAgentAudioPromptFileGuid),
          };

          return { ...this._formEntity, audioSettings, userGuids, overflowSettings, timeoutSettings, noAgentSettings };
        }),
        switchMap((body) => this._callQueuesResource.post(tenantId, body)),
        catchError((error) => {
          this._bottomNavService.setButtonPendingState('save-button', false);
          this._bottomNavService.setButtonDisabledState('back-button', false);
          return throwError(() => error);
        }),
      )
      .subscribe((callQueueRef) => {
        this._toastService.pushSaveToast(
          'tkey;teams_call_queues.list_view.toast.service_type',
          this._formEntity.name,
          ZiroIcons.CALL_QUEUE,
        );
        this._router.navigate(['/call-queue-management/tenants', tenantId, 'teams-call-queues', callQueueRef.id]);
      });
    this._subscriptions.add(sub);
  }
}
